import styled from "@emotion/styled";
import { isEmpty } from "lodash";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import { Add, Info } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken, Tag, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { withVar } from "themes/helpers";

import { OpenedAccordionIndex, TriggerFieldArrayElement } from "../../interfaces";

import { TriggerPicker } from "./TriggerPicker";
import { TRIGGER_EMPTY_STATE_TEXT } from "./utils";

interface IProps {
  /**
   * Ref object for the trigger pickers. Used to scroll to the first error on
   * submit.
   */
  triggerPickerRefs?: React.RefObject<Array<HTMLDivElement | null>>;
  openedAccordionIndex: OpenedAccordionIndex;
  setOpenedAccordionIndex: Dispatch<SetStateAction<OpenedAccordionIndex>>;
}

export const TriggerPickerList: FC<IProps> = ({ triggerPickerRefs, openedAccordionIndex, setOpenedAccordionIndex }) => {
  const { values } = useFormState();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <FieldArray<TriggerFieldArrayElement> name="triggers">
      {({ fields, meta }) => {
        const hasOnlyEmptyTriggers = fields.value
          ? fields.value.filter(trigger => !isEmpty(trigger)).length === 0
          : true;

        const shouldShowError = meta.submitFailed && hasOnlyEmptyTriggers;

        const handleModalOpen = (): void => {
          fields.push({});
          setModalOpen(true);
        };

        const handleModalClose = (): void => {
          setModalOpen(false);
        };

        return (
          <Flex column marginBottom={withVar(SpacingToken.Spacing5)}>
            {fields.length === 0 && <EmptyStateText size="sm">{TRIGGER_EMPTY_STATE_TEXT}</EmptyStateText>}
            {fields.map((name, idx) => {
              // display quantifier tag only if next trigger value is defined
              const displayQuantifierTag = !isEmpty(fields.value[idx + 1]);

              return (
                <div
                  key={name}
                  data-testid={`trigger-field-${idx}`}
                  ref={el => {
                    if (triggerPickerRefs?.current) {
                      triggerPickerRefs.current[idx] = el;
                    }
                  }}
                >
                  <TriggerPicker
                    index={idx}
                    fields={fields}
                    onModalClose={() => handleModalClose()}
                    isModalOpen={modalOpen}
                    openedIndex={openedAccordionIndex}
                    setOpenedIndex={setOpenedAccordionIndex}
                  />
                  {!isEmpty(fields.value[idx]) && (
                    <Divider withTagInside={displayQuantifierTag}>
                      {displayQuantifierTag && (
                        <QuantifierTag kind="gray" size="small">
                          {values.quantifier === "all" ? "And" : "Or"}
                        </QuantifierTag>
                      )}
                    </Divider>
                  )}
                </div>
              );
            })}
            <Flex width="100%">
              <AddNewAction
                kind="high-contrast"
                onClick={handleModalOpen}
                size="compact"
                icon={<Icon source={Add} />}
                aligned={hasOnlyEmptyTriggers}
                data-testid="trigger-picker"
              />
              {shouldShowError && (
                <Flex
                  alignItems="center"
                  gap={withVar(SpacingToken.Spacing1)}
                  marginLeft={withVar(SpacingToken.Spacing3)}
                >
                  <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicNegative)} />
                  <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                    At least one condition must be added.
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        );
      }}
    </FieldArray>
  );
};

const EmptyStateText = styled(Text)`
  margin-bottom: var(${SpacingToken.Spacing3});
  color: var(${DesignToken.ContentBasicDisabled});
`;

const AddNewAction = styled(Button)<{ aligned: boolean }>`
  margin-left: ${({ aligned }): string => (aligned ? "0" : withVar(SpacingToken.Spacing3))};
`;

const Divider = styled.div<{ withTagInside?: boolean }>`
  position: relative;
  min-height: ${({ withTagInside }): string =>
    withTagInside ? withVar(SpacingToken.Spacing12) : withVar(SpacingToken.Spacing4)};
  border-right: 1px dashed ${withVar(DesignToken.BorderBasicPrimary)};
  width: var(${SpacingToken.Spacing7});
  display: flex;
  align-items: center;
`;

const QuantifierTag = styled(Tag)`
  position: absolute;
  right: 0;
  transform: translateX(50%);
  font-weight: bold;
`;
